import { z } from "zod";
import { Created, Id, Ok, OptionalText } from "./common";
import { defineRoutes } from "./define";
import { PROBLEMS } from "./problem";
import { TelegramLink } from "./telegram";

/** Mirrors core/modules/patients/domain/patient-status.ts (the contract is the client-facing copy). */
export const PATIENT_STATUSES = [
  "new",
  "active",
  "paused",
  "completed",
  "maintenance",
  "archived",
] as const;
export const PatientStatus = z.enum(PATIENT_STATUSES);
export const PatientView = z.enum(["all", "active", "action", "event", "paused", "archived"]);

export const PatientListItem = z.object({
  id: Id,
  name: z.string(),
  status: PatientStatus,
  sport: z.string().nullable(),
});
export type PatientListItem = z.infer<typeof PatientListItem>;

export const PatientSnapshot = z.object({
  id: Id,
  /** Given and family name — both required. */
  firstName: z.string(),
  lastName: z.string(),
  /** "Імʼя Прізвище", formatted by the API (kit/person-name) — never edited as one field. */
  name: z.string(),
  status: PatientStatus,
  specialistId: z.string().nullable(),
  sport: z.string().nullable(),
  phone: z.string().nullable(),
  telegram: z.string().nullable(),
  instagram: z.string().nullable(),
  email: z.string().nullable(),
  serviceDetails: z.string().nullable(),
  internalNotes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PatientSnapshot = z.infer<typeof PatientSnapshot>;

/** The card's write shape; the domain (`PersonName`) normalises the two name parts again. */
export const CreatePatientBody = z.object({
  firstName: z.string().trim().min(1, "Імʼя пацієнта обовʼязкове").max(80),
  lastName: z.string().trim().min(1, "Прізвище пацієнта обовʼязкове").max(80),
  status: PatientStatus.optional(),
  sport: OptionalText(120),
  phone: OptionalText(40),
  telegram: OptionalText(80),
  instagram: OptionalText(80),
  // A typo here used to be silent — the patient simply never got an email.
  email: OptionalText(160).refine(
    (v) => v === undefined || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
    "Email виглядає некоректно",
  ),
  serviceDetails: OptionalText(2000),
  internalNotes: OptionalText(2000),
  specialistId: OptionalText(64),
});
export type CreatePatientBody = z.infer<typeof CreatePatientBody>;

export const UpdatePatientBody = CreatePatientBody.omit({ specialistId: true });
export type UpdatePatientBody = z.infer<typeof UpdatePatientBody>;

export const patients = defineRoutes({
  list: {
    method: "GET",
    path: "/patients",
    summary: "Every patient (the rail / list screens filter client-side by view)",
    responses: { 200: z.array(PatientListItem), 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  search: {
    method: "GET",
    path: "/patients/search",
    summary: "⌘K search: top 8 matches",
    query: z.object({ q: z.string().max(120).default(""), view: PatientView.optional() }),
    responses: { 200: z.array(PatientListItem), 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  get: {
    method: "GET",
    path: "/patients/:patientId",
    summary: "One patient's card",
    params: z.object({ patientId: Id }),
    responses: { 200: PatientSnapshot, 401: PROBLEMS[401], 403: PROBLEMS[403], 404: PROBLEMS[404] },
  },
  create: {
    method: "POST",
    path: "/patients",
    summary: "Register a patient",
    body: CreatePatientBody,
    responses: {
      201: Created,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      422: PROBLEMS[422],
    },
  },
  update: {
    method: "PATCH",
    path: "/patients/:patientId",
    summary: "Edit the card",
    params: z.object({ patientId: Id }),
    body: UpdatePatientBody,
    responses: {
      200: Ok,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
  telegramLink: {
    method: "POST",
    path: "/patients/:patientId/telegram-link",
    summary: "Mint a one-time code + deep link that binds the patient's Telegram (24 h)",
    params: z.object({ patientId: Id }),
    responses: { 201: TelegramLink, 401: PROBLEMS[401], 403: PROBLEMS[403], 404: PROBLEMS[404] },
  },
  archive: {
    method: "POST",
    path: "/patients/:patientId/archive",
    summary: "Archive (or restore) a patient",
    params: z.object({ patientId: Id }),
    body: z.object({ archived: z.boolean().default(true) }),
    responses: {
      200: Ok,
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
});
